import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { formatVehicleNameFromObject } from '@/utils/vehicleName';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  FileText, Download, AlertTriangle, CheckCircle, 
  Wrench, ClipboardCheck, ShieldAlert, User, Calendar,
  Car, Gauge, Info, ChevronDown, ChevronRight, Loader2,
  FileWarning, Printer
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function PremiumDiagnosticReport({ jobId, onClose }) {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedSections, setExpandedSections] = useState({
    methodology: true,
    evidence: true,
    risk: true,
    rectification: true,
    validation: true
  });
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const printRef = useRef(null);

  useEffect(() => {
    loadReport();
  }, [jobId]);

  const loadReport = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnostic-report/${jobId}/premium`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReportData(res.data);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load diagnostic report');
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    try {
      setDownloadingPdf(true);
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/api/diagnostic-report/${jobId}/premium/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `diagnostic_report_${jobId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to download PDF:', err);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadingPdf(false);
    }
  };

  const handlePrint = () => {
    // Open print dialog for the report content
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Diagnostic Investigation Report - ${reportData?.header?.vehicle?.registration || 'Report'}</title>
          <style>
            * { box-sizing: border-box; margin: 0; padding: 0; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 20px; color: #1e293b; }
            .header { background: #1e293b; color: white; padding: 20px; margin: -20px -20px 20px -20px; }
            .header h1 { font-size: 18px; margin-bottom: 5px; }
            .header .subtitle { color: #94a3b8; font-size: 12px; }
            .vehicle-info { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; margin-top: 15px; padding-top: 15px; border-top: 1px solid rgba(255,255,255,0.2); }
            .vehicle-info .label { color: #94a3b8; font-size: 10px; text-transform: uppercase; }
            .vehicle-info .value { font-weight: 600; }
            .section { margin-bottom: 20px; page-break-inside: avoid; }
            .section-header { display: flex; align-items: center; gap: 10px; margin-bottom: 10px; }
            .section-number { width: 24px; height: 24px; border-radius: 50%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; font-weight: bold; font-size: 12px; }
            .section-title { font-size: 14px; font-weight: 600; }
            .content-box { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 8px; padding: 15px; }
            .grid-2 { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }
            .label { color: #64748b; font-size: 11px; margin-bottom: 3px; }
            .value { font-weight: 500; }
            .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 10px; font-weight: 600; }
            .badge-high { background: #dcfce7; color: #166534; }
            .badge-medium { background: #fef3c7; color: #92400e; }
            .badge-low { background: #fee2e2; color: #991b1b; }
            ul { padding-left: 20px; }
            li { margin-bottom: 5px; font-size: 13px; }
            @media print {
              body { padding: 0; }
              .header { margin: 0 0 20px 0; }
            }
          </style>
        </head>
        <body>
          ${printContent.innerHTML}
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const getConfidenceColor = (level) => {
    if (level === 'HIGH') return 'bg-green-100 text-green-800 border-green-200';
    if (level === 'MEDIUM') return 'bg-amber-100 text-amber-800 border-amber-200';
    return 'bg-red-100 text-red-800 border-red-200';
  };

  const getRiskColor = (level) => {
    if (level === 'HIGH') return 'text-red-600 bg-red-50';
    if (level?.includes('MEDIUM')) return 'text-amber-600 bg-amber-50';
    return 'text-green-600 bg-green-50';
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-3xl mx-4 bg-white">
          <CardContent className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
            <span className="ml-3 text-slate-600">Loading diagnostic report...</span>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
        <Card className="w-full max-w-lg mx-4 bg-white">
          <CardContent className="py-10 text-center">
            <FileWarning className="w-12 h-12 mx-auto text-red-400 mb-4" />
            <h3 className="text-lg font-semibold text-slate-800 mb-2">Unable to Load Report</h3>
            <p className="text-slate-600 mb-4">{error}</p>
            <Button onClick={onClose} variant="outline">Close</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!reportData) return null;

  const { header, executive_summary, methodology, root_cause_analysis, evidence, risk_assessment, rectification, validation_protocol, certification, disclaimer } = reportData;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 overflow-y-auto" data-testid="premium-diagnostic-report-modal">
      <div className="w-full max-w-4xl bg-white rounded-xl shadow-2xl my-4">
        {/* Printable content wrapper */}
        <div ref={printRef}>
        {/* Header */}
        <div className="header bg-gradient-to-r from-slate-800 to-slate-900 text-white p-6 rounded-t-xl print:rounded-none">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/10 rounded-lg print:hidden">
                <FileText className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold tracking-tight">DIAGNOSTIC INVESTIGATION REPORT</h1>
                <p className="subtitle text-slate-300 text-sm">Premium Technical Documentation</p>
              </div>
            </div>
            <div className="flex items-center gap-2 print:hidden">
              <Button 
                onClick={handlePrint}
                variant="secondary"
                className="bg-white/10 hover:bg-white/20 text-white border-0"
                data-testid="print-premium-report-btn"
              >
                <Printer className="w-4 h-4 mr-2" />
                Print
              </Button>
              <Button 
                onClick={downloadPdf} 
                disabled={downloadingPdf}
                variant="secondary"
                className="bg-white/10 hover:bg-white/20 text-white border-0"
                data-testid="download-premium-pdf-btn"
              >
                {downloadingPdf ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Download PDF
              </Button>
              <Button onClick={onClose} variant="ghost" className="text-white hover:bg-white/10">
                Close
              </Button>
            </div>
          </div>

          {/* Vehicle Info Bar */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-white/20">
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Registration</div>
              <div className="font-semibold text-lg">{header.vehicle.registration}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Vehicle</div>
              <div className="font-medium">{formatVehicleNameFromObject(header.vehicle)}</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Mileage</div>
              <div className="font-medium">{header.vehicle.mileage?.toLocaleString() || 'N/A'} miles</div>
            </div>
            <div>
              <div className="text-slate-400 text-xs uppercase tracking-wider mb-1">Reference</div>
              <div className="font-mono text-sm">{header.reference_number}</div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(100vh-300px)] overflow-y-auto">
          {/* Section 1: Customer Concern */}
          <div className="mb-6" data-testid="customer-concern-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">1</div>
              <h2 className="text-lg font-semibold text-slate-800">Customer Reported Concern</h2>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              <p className="text-slate-700 italic">"{header.customer_concern}"</p>
            </div>
          </div>

          {/* Section 2: Executive Summary */}
          <div className="mb-6" data-testid="executive-summary-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">2</div>
              <h2 className="text-lg font-semibold text-slate-800">Executive Technical Summary</h2>
            </div>
            <div className="bg-white rounded-lg border border-slate-200 p-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <div className="text-sm text-slate-500 mb-1">Root Cause</div>
                  <p className="font-medium text-slate-800">{executive_summary.root_cause}</p>
                </div>
                <div>
                  <div className="text-sm text-slate-500 mb-1">Confidence Level</div>
                  <div className="flex items-center gap-2">
                    <Badge className={`${getConfidenceColor(executive_summary.confidence_level.level)} px-3 py-1`}>
                      {executive_summary.confidence_level.level} ({executive_summary.confidence_level.score}%)
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-600 mt-1">{executive_summary.confidence_level.description}</p>
                </div>
              </div>
              {executive_summary.confidence_level.factors?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-slate-100">
                  <div className="text-sm text-slate-500 mb-2">Evidence Factors</div>
                  <div className="flex flex-wrap gap-2">
                    {executive_summary.confidence_level.factors.map((factor, i) => (
                      <Badge key={i} variant="outline" className="text-slate-600 bg-slate-50">
                        <CheckCircle className="w-3 h-3 mr-1 text-green-500" />
                        {factor}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Section 3: Investigation Methodology */}
          <div className="mb-6" data-testid="methodology-section">
            <button 
              onClick={() => toggleSection('methodology')}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">3</div>
              <h2 className="text-lg font-semibold text-slate-800 flex-1">Investigation Methodology</h2>
              {expandedSections.methodology ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>
            {expandedSections.methodology && (
              <div className="space-y-3 ml-10">
                {methodology.phases.map((phase, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                    <div>
                      <div className="font-medium text-slate-800">Phase {phase.number}: {phase.title}</div>
                      <p className="text-sm text-slate-600">{phase.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Section 4: Root Cause Analysis (from polished report) */}
          <div className="mb-6" data-testid="root-cause-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">4</div>
              <h2 className="text-lg font-semibold text-slate-800">Confirmed Root Cause Analysis</h2>
            </div>
            <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
              {root_cause_analysis.technical_explanation ? (
                <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">
                  {root_cause_analysis.technical_explanation}
                </pre>
              ) : (
                <p className="text-slate-500 italic">Technical analysis pending</p>
              )}
            </div>
          </div>

          {/* Section 5: Evidence Record */}
          <div className="mb-6" data-testid="evidence-section">
            <button 
              onClick={() => toggleSection('evidence')}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">5</div>
              <h2 className="text-lg font-semibold text-slate-800 flex-1">Evidence Record</h2>
              {expandedSections.evidence ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>
            {expandedSections.evidence && (
              <div className="space-y-4 ml-10">
                {/* DTC Codes */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2">Diagnostic Trouble Codes</h4>
                  {evidence.dtc_codes?.length > 0 ? (
                    <div className="space-y-2">
                      {evidence.dtc_codes.map((dtc, i) => (
                        <div key={i} className="flex items-center gap-2 bg-white rounded p-2 border border-slate-200">
                          <Badge variant="outline" className="font-mono">{dtc.code || 'N/A'}</Badge>
                          <span className="text-sm text-slate-600">{dtc.description || 'No description'}</span>
                          <Badge className={`ml-auto ${dtc.status === 'current' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'}`}>
                            {dtc.status || 'stored'}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No fault codes recorded</p>
                  )}
                </div>

                {/* Live Data */}
                {evidence.live_data_readings?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Live Data Readings</h4>
                    <div className="grid grid-cols-2 gap-2">
                      {evidence.live_data_readings.slice(0, 6).map((reading, i) => (
                        <div key={i} className="bg-white rounded p-2 border border-slate-200 text-sm">
                          <span className="text-slate-500">{reading.parameter}:</span>{' '}
                          <span className="font-medium">{reading.value} {reading.unit}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Photos */}
                {evidence.photos?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-medium text-slate-700 mb-2">Photographic Evidence</h4>
                    <Badge variant="outline">{evidence.photos.length} photo(s) captured</Badge>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 6: Risk Assessment */}
          <div className="mb-6" data-testid="risk-section">
            <button 
              onClick={() => toggleSection('risk')}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">6</div>
              <h2 className="text-lg font-semibold text-slate-800 flex-1">Risk & Progression Assessment</h2>
              {expandedSections.risk ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>
            {expandedSections.risk && (
              <div className="ml-10">
                <div className={`rounded-lg p-4 ${getRiskColor(risk_assessment.risk_level)}`}>
                  <div className="flex items-center gap-2 mb-2">
                    <ShieldAlert className="w-5 h-5" />
                    <span className="font-semibold">Risk Level: {risk_assessment.risk_level}</span>
                  </div>
                  <p className="mb-3">{risk_assessment.risk_description}</p>
                  
                  <div className="mt-4 pt-4 border-t border-current/20">
                    <div className="font-medium mb-2">Consequences if Unresolved:</div>
                    <ul className="space-y-1">
                      {risk_assessment.consequences_if_unresolved?.map((consequence, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                          {consequence}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Section 7: Rectification Specification */}
          <div className="mb-6" data-testid="rectification-section">
            <button 
              onClick={() => toggleSection('rectification')}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">7</div>
              <h2 className="text-lg font-semibold text-slate-800 flex-1">Rectification Specification</h2>
              {expandedSections.rectification ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>
            {expandedSections.rectification && (
              <div className="space-y-4 ml-10">
                {/* Confirmed Parts */}
                <div>
                  <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                    <Wrench className="w-4 h-4" />
                    Confirmed Failed Parts
                  </h4>
                  {rectification.confirmed_parts?.length > 0 ? (
                    <div className="space-y-2">
                      {rectification.confirmed_parts.map((part, i) => (
                        <div key={i} className="bg-red-50 rounded p-3 border border-red-100">
                          <div className="font-medium text-red-800">{part.part_name || 'Unknown part'}</div>
                          {part.reason && <div className="text-sm text-red-600">{part.reason}</div>}
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500 italic">No parts confirmed failed</p>
                  )}
                </div>

                {/* Labour */}
                {rectification.estimated_labour_hours && (
                  <div className="bg-slate-50 rounded p-3 border border-slate-200">
                    <div className="text-sm text-slate-500">Estimated Repair Labour</div>
                    <div className="font-medium text-slate-800">{rectification.estimated_labour_hours} hours</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Section 8: Post-Repair Validation Protocol */}
          <div className="mb-6" data-testid="validation-section">
            <button 
              onClick={() => toggleSection('validation')}
              className="flex items-center gap-2 mb-3 w-full text-left"
            >
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">8</div>
              <h2 className="text-lg font-semibold text-slate-800 flex-1">Post-Repair Validation Protocol</h2>
              {expandedSections.validation ? <ChevronDown className="w-5 h-5 text-slate-400" /> : <ChevronRight className="w-5 h-5 text-slate-400" />}
            </button>
            {expandedSections.validation && (
              <div className="ml-10">
                <div className="space-y-2">
                  {validation_protocol.road_test_required && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="w-4 h-4 text-green-600" />
                      Road test to verify fault resolution
                    </div>
                  )}
                  {validation_protocol.live_data_check && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="w-4 h-4 text-green-600" />
                      Live data confirmation of normal operation
                    </div>
                  )}
                  {validation_protocol.dtc_rescan && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="w-4 h-4 text-green-600" />
                      DTC erase and re-scan after test drive
                    </div>
                  )}
                  {validation_protocol.regen_reset && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="w-4 h-4 text-green-600" />
                      DPF regeneration reset
                    </div>
                  )}
                  {validation_protocol.adaptation_reset && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="w-4 h-4 text-green-600" />
                      ECU adaptation reset
                    </div>
                  )}
                  {validation_protocol.other_requirements && (
                    <div className="flex items-center gap-2 text-sm">
                      <ClipboardCheck className="w-4 h-4 text-green-600" />
                      {validation_protocol.other_requirements}
                    </div>
                  )}
                  {!validation_protocol.road_test_required && !validation_protocol.live_data_check && !validation_protocol.dtc_rescan && (
                    <div className="flex items-center gap-2 text-sm text-slate-500">
                      <ClipboardCheck className="w-4 h-4" />
                      Standard quality control check
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Section 9: Technician Certification */}
          <div className="mb-6" data-testid="certification-section">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-bold text-sm">9</div>
              <h2 className="text-lg font-semibold text-slate-800">Technician Certification</h2>
            </div>
            <div className="bg-slate-800 text-white rounded-lg p-4">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                  <User className="w-6 h-6" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-lg">{certification.technician_name}</div>
                  <div className="text-slate-300 text-sm">{certification.technician_title}</div>
                  <div className="flex items-center gap-2 text-slate-400 text-sm mt-1">
                    <Calendar className="w-4 h-4" />
                    {certification.submission_date ? new Date(certification.submission_date).toLocaleDateString() : 'N/A'}
                  </div>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-white/20">
                <p className="text-sm text-slate-300 italic">
                  "{certification.statement}"
                </p>
              </div>
            </div>
          </div>

          {/* Disclaimer */}
          <div className="bg-slate-50 rounded-lg p-4 border border-slate-200">
            <div className="flex items-start gap-2">
              <Info className="w-5 h-5 text-slate-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-slate-500">{disclaimer}</p>
            </div>
          </div>
        </div>
        </div> {/* End printRef wrapper */}
      </div>
    </div>
  );
}
